import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, memo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import Skeleton from 'react-loading-skeleton';

import { IconRcFeedRank } from '@/assets/stream';
import {
  EmptyRecord,
  Modal,
  FollowButton,
  UserCardPopup,
  HelperTooltip,
  UserAvatar,
} from '@/components';
import { useCurrentUserId, openNewWindow, formatNetworth, shortenAddress } from '@/utils';
import style from './index.module.scss';
import { useProposalVoters } from './hooks';
import { ProposalVoter } from '@/views/Proposal/types';

const { CommonModal } = Modal;

export const ProposalVoterModal = ({
  id,
  on,
  turnOff,
}: {
  id: string;
  on?: boolean;
  turnOff?: () => void;
}) => {
  const { t } = useTranslation();
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useProposalVoters(id);
  const list = useMemo(() => data?.pages?.flatMap(x => x?.voters), [data?.pages]);
  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <CommonModal on={on} turnOff={turnOff}>
      <div className={style.title}>
        {data ? t('ProposalVoterModal.voter', { count: data?.pages?.[0]?.total_count }) : null}
      </div>
      <div>
        <div className={style.tableHeader}>
          <div className={style.leftContent}>{t('ProposalVoterModal.1kpnv')}</div>
          <div>{t('ProposalVoterModal.q6o2ej')}</div>
        </div>
        {isLoading ? (
          <SkeletonList />
        ) : list?.length ? (
          <Virtuoso
            style={{ height: '355px' }}
            data={list}
            overscan={10}
            endReached={fetchMore}
            itemContent={(index, item) => <ListItem data={item} key={`${item.id}_${index}`} />}
          />
        ) : (
          <EmptyRecord />
        )}
      </div>
    </CommonModal>
  );
};

const ListItem = ({ data }: { data: ProposalVoter }) => {
  const { t } = useTranslation();

  const address = useCurrentUserId();
  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      openNewWindow(`/profile/${data.id}/stream`, '_blank');
    },
    [data],
  );

  return (
    <div className={style.item}>
      <div className={style.leftContent}>
        <div onClick={handleUserClick} data-id={data.id}>
          <UserAvatar className={style.avatar} size={48} data={data?.desc} userCardPopup />
        </div>
        <div className={style.userInfo}>
          <div className={style.commentHead}>
            <UserCardPopup address={data.id}>
              <div onClick={handleUserClick} className={style.uid}>
                {data?.desc?.user?.web3_id || shortenAddress(data?.desc?.id!)}
              </div>
            </UserCardPopup>
          </div>
          <HelperTooltip title={t('ProposalVoterModal.1h0gg1w')}>
            <div className={style.content}>
              <IconRcFeedRank /> <span>{data?.rank_at}</span>
            </div>
          </HelperTooltip>
        </div>
      </div>
      <div className={style.netWorth}>{formatNetworth(data?.desc?.usd_value)}</div>
      <div className={style.op}>
        <div className={style.followBtnWrap}>
          {data.id === address ? null : (
            <FollowButton address={data.id} userInfo={data as any} className={style.followBtn} />
          )}
        </div>
      </div>
    </div>
  );
};

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array(count).fill(
      <div className={style.item}>
        <div className={style.leftContent}>
          <Skeleton circle height={48} width={48} className={style.avatar} />
          <div className={style.userInfo}>
            <Skeleton width={80} height={18} className={style.commentHead} />
            <Skeleton width={40} height={18} className={style.commentHead} />
          </div>
        </div>
        <Skeleton className={style.followBtn} />
      </div>,
    )}
  </>
));
